'use client';

// module imports
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

// file imports
import { formatDate } from '@/lib/helpers';
import { useAppDispatch } from '@/redux/hooks';
import { updateEvent } from '@/redux/slices/eventSlice';
import { IEvent, IUser } from '@/types';
import { FeedbackQRCode, SpeakerSearchPopup } from '@/components';

interface EventDetailFieldProps {
  label: string;
  value: string | number | null | undefined;
  spanFull?: boolean;
}

function EventDetailField({ label, value, spanFull = false }: EventDetailFieldProps) {
  const displayValue = value || <span className="text-gray-400">N/A</span>;
  const spanClass = spanFull ? 'md:col-span-2' : '';

  return (
    <div className={spanClass}>
      <span className="font-semibold block text-gray-700 text-sm sm:text-base">{label}:</span>
      <span className="text-gray-900 text-sm sm:text-base">{displayValue}</span>
    </div>
  );
}

export default function EventDetailsSection({ event }: { event: IEvent }) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [isSpeakerSearchOpen, setIsSpeakerSearchOpen] = useState(false);

  const currentSpeakers = event.speakers || [];

  const handleSpeakerSearch = () => {
    setIsSpeakerSearchOpen(true);
  };

  const handleSpeakerSelect = async (speaker: IUser) => {
    try {
      const currentSpeakerIds = event.speakers?.map((s) => s._id) || [];
      const updatedSpeakers = [...currentSpeakerIds, speaker._id];

      await dispatch(updateEvent({ eventSlug: event.slug || '', updatedData: { speakers: updatedSpeakers } })).unwrap();
    } catch (error) {
      console.error('Failed to add speaker to event:', error);
    }
  };

  const handleRemoveSpeaker = async (speakerId: string) => {
    try {
      const currentSpeakerIds = event.speakers?.map((s) => s._id) || [];
      const updatedSpeakers = currentSpeakerIds.filter((id) => id !== speakerId);

      await dispatch(updateEvent({ eventSlug: event.slug || '', updatedData: { speakers: updatedSpeakers } })).unwrap();
    } catch (error) {
      console.error('Failed to remove speaker from event:', error);
    }
  };

  const handleCloseSpeakerSearch = () => {
    setIsSpeakerSearchOpen(false);
  };

  return (
    <>
      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow flex flex-col gap-4 sm:gap-6 w-full md:w-3/4 mx-auto mb-4">
        {/* Header with edit button */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Event Details</h2>
          <Link href={`/events/${event.slug}/edit`} className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-xs sm:text-sm font-medium">Edit</span>
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-y-4 gap-x-2 md:gap-x-8">
          <EventDetailField label="Name of Event" value={event.title} />
          <EventDetailField label="Start Date" value={formatDate(event.startDate.toString())} />
          <EventDetailField label="End Date" value={formatDate(event.endDate.toString())} />
          <EventDetailField label="Venue" value={event.venue} />
          <EventDetailField label="No. of Speakers" value={event.numberOfSpeakers} />
          <EventDetailField label="Event ID" value={event.slug} spanFull />

          {/* Selected Speakers Display */}
          {currentSpeakers.length > 0 && (
            <div className="md:col-span-2">
              <span className="font-semibold block text-gray-700 mb-2 text-sm sm:text-base">Selected Speakers:</span>
              <div className="flex flex-wrap gap-2">
                {currentSpeakers.map((speaker, index) => (
                  <div key={`${speaker._id}-${index}`} className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm flex items-center gap-2">
                    <span>{speaker.name || speaker.email}</span>
                    <button onClick={() => handleRemoveSpeaker(speaker._id)} className="text-blue-600 hover:text-blue-800 font-bold cursor-pointer">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={handleSpeakerSearch} className="bg-[#5167b2] text-white font-bold px-3 sm:px-8 py-1.5 sm:py-3 rounded-lg sm:rounded-xl shadow hover:bg-[#4158a2] transition cursor-pointer flex items-center gap-1 sm:gap-2 text-xs sm:text-base">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full flex items-center justify-center">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#5167b2] rounded-full"></div>
            </div>
            Search for Speakers
            {currentSpeakers.length > 0 && <span className="bg-white text-[#5167b2] text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ml-1 sm:ml-2">{currentSpeakers.length}</span>}
          </button>
          <button onClick={() => router.push('/events/new')} className="bg-[#5167b2] text-white font-bold px-3 sm:px-8 py-1.5 sm:py-3 rounded-lg sm:rounded-xl shadow hover:bg-[#4158a2] transition cursor-pointer text-xs sm:text-base">
            Create Event
          </button>
        </div>
      </div>
      <div></div>
      <FeedbackQRCode eventSlug={event.slug || ''} title="Event QR Code" />
      <SpeakerSearchPopup isOpen={isSpeakerSearchOpen} onClose={handleCloseSpeakerSearch} onSelectSpeaker={handleSpeakerSelect} currentSpeakers={currentSpeakers.map((s) => s._id)} />
    </>
  );
}
